import { useSyncExternalStore } from 'react';
import { subscribeToScopes, getScopesSnapshot, getScopesByAbaSnapshot } from '../data/scope.store';

const EMPTY_SCOPES = [];

/**
 * Reactive access to the current session's scopes array (from GET /api/user,
 * cached on DataController.Instance().user.scopes). Re-renders on login/logout
 * via the existing ClockoutController auth-subscriber channel.
 * @returns {string[]}
 */
export function useScopes() {
    return useSyncExternalStore(subscribeToScopes, getScopesSnapshot, () => EMPTY_SCOPES);
}

/**
 * Scopes for a specific FI, for multi-FI users switching context. Falls back
 * to the primary-FI `scopes` array when no aba is given.
 * @param {string} [aba]
 * @returns {string[]}
 */
export function useScopesForAba(aba) {
    const byAba = useSyncExternalStore(subscribeToScopes, getScopesByAbaSnapshot, () => ({}));
    const primary = useScopes();
    if (!aba) return primary;
    return byAba[aba] ?? EMPTY_SCOPES;
}

/**
 * @param {string} scope
 * @param {{ aba?: string }} [options]
 * @returns {boolean}
 */
export function useHasScope(scope, options) {
    const scopes = useScopesForAba(options?.aba);
    return scopes.includes(scope);
}

/**
 * @param {string[]} scopes
 * @param {'any' | 'all'} [mode='any']
 * @param {{ aba?: string }} [options]
 * @returns {boolean}
 */
export function useHasScopes(scopes, mode = 'any', options) {
    const current = useScopesForAba(options?.aba);
    if (!Array.isArray(scopes) || !scopes.length) return true; // parity with hasRoleAccess(null) semantics
    return mode === 'all'
        ? scopes.every((s) => current.includes(s))
        : scopes.some((s) => current.includes(s));
}
